/**
 * Soulog customization: auto-hide the header on the product page's first screen (2026-09-16)
 *
 * There is only one purpose: the product page's first screen is a full-screen KV video, and the white frosted-glass nav bar on top of it covers the top edge of the frame.
 * So while "the page is still on the first screen", after 2s of idleness the header slides up, handing the KV back to the video in full.
 *
 * Behavior boundaries (deliberately narrow):
 *   - **Only scrolling** permanently disables this script. As soon as the user scrolls, the header slides right back, and from then on it is purely
 *     the theme's default sticky behavior -- no more "hide on every little scroll" jitter.
 *   - Mouse moving to the top of the viewport / keyboard focus entering the header = the user wants the menu: bring it back immediately and **pause** the timer
 *     (pulling the menu away while the mouse is still on it is the most annoying thing); once the mouse leaves the top zone or focus leaves the header,
 *     restart the 2s timer and hide again -- because the page is still on the first screen, the KV should still give way to the video.
 *   - Not hidden inside the theme editor (designMode), so the ops team does not think the header has gone missing.
 *   - If the browser restored a scroll position on page entry (scrollY > 0), it does not start at all.
 *
 * Home page starts hidden (2026-09-28): header.liquid renders `header-autohide--start-hidden header-autohide--hidden`
 * on the index template, so the header is already gone on first paint (adding it from JS made it flash in, then slide away).
 * This script then skips the initial 2s timer; everything else (scroll releases, top-zone / focus wakes) is unchanged.
 * Because the hidden state is server-rendered, every early return below must release it first.
 *
 * Implementation note: the offset is applied to .header, **not** .header-section -- header.js's --header-offset
 * reads .header-section's rect.top; only applying the offset to the child element avoids polluting that variable (which in turn affects
 * the mobile menu height and product-grid's sticky top).
 */
(function () {
	const HIDE_DELAY = 2000; // how long to stay idle before hiding (ms)
	const TOP_ZONE = 80; // the mouse entering this many px at the top of the viewport counts as "wants the menu" (uses the header height when the header is taller)

	function init() {
		const header = document.querySelector(".header.header-autohide--enabled");
		if (!header) return;
		const startHidden = header.classList.contains("header-autohide--start-hidden");
		// Early exits must undo the server-rendered hidden state, or the header would stay gone for good
		const bail = () => header.classList.remove("header-autohide--hidden");
		if (window.Shopify && window.Shopify.designMode) return bail();
		if (window.scrollY > 0) return bail(); // after a refresh we're mid-page, so skip the first-screen behavior entirely

		let timer = null;
		let released = false; // has scrolled = permanently disabled
		let pointerAtTop = false;
		let focusInHeader = false;

		const clear = () => {
			if (timer) {
				window.clearTimeout(timer);
				timer = null;
			}
		};
		const show = () => header.classList.remove("header-autohide--hidden");
		const hide = () => {
			timer = null;
			if (released || pointerAtTop || focusInHeader) return;
			if (window.scrollY > 0) return;
			header.classList.add("header-autohide--hidden");
		};
		/** Restart the timer. Nothing is scheduled while any "user is using the menu" condition blocks it */
		const schedule = () => {
			clear();
			if (released || pointerAtTop || focusInHeader) return;
			timer = window.setTimeout(hide, HIDE_DELAY);
		};
		/** Bring back and hold (the caller decides when to schedule again) */
		const wake = () => {
			clear();
			show();
		};

		const onScroll = () => {
			// The only permanent-disable entry point: the user has started browsing the page, so the header returns to normal sticky behavior
			released = true;
			wake();
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseleave", onMouseLeaveDoc);
			header.removeEventListener("focusin", onFocusIn);
			header.removeEventListener("focusout", onFocusOut);
		};
		const onMouseMove = (e) => {
			const zone = Math.max(header.offsetHeight || 0, TOP_ZONE);
			const atTop = e.clientY <= zone;
			if (atTop === pointerAtTop) return; // only act when crossing the boundary
			pointerAtTop = atTop;
			if (atTop) wake();
			else schedule();
		};
		// Once the mouse leaves the browser window there are no more mousemove events; if the last one was at the top, the header would get stuck visible
		const onMouseLeaveDoc = () => {
			if (!pointerAtTop) return;
			pointerAtTop = false;
			schedule();
		};
		const onFocusIn = () => {
			focusInHeader = true;
			wake();
		};
		const onFocusOut = () => {
			focusInHeader = false;
			schedule();
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("mousemove", onMouseMove, { passive: true });
		document.addEventListener("mouseleave", onMouseLeaveDoc);
		header.addEventListener("focusin", onFocusIn);
		header.addEventListener("focusout", onFocusOut);

		// Home page is already hidden by the markup; only other templates start the "idle 2s then hide" timer
		if (!startHidden) schedule();
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
